import { EventDispatcher } from "../Events/EventDispatcher";
import { SaveEventArgs } from "../Common/SaveEventArgs";
import { BaseControl } from "./BaseControl";
import { TextControl } from "./TextControl";
import { DateControl } from "./DateControl";
import { ImageCheckControl } from "./ImageCheckControl";
import { CheckBoxControl } from "./CheckBoxControl";
import { NumberControl } from "./NumberControl";
import { MultilineControl } from "./MultilineControl";
import { SelectControl } from "./SelectControl";
import { RadioGroupControl } from "./RadioGroupControl";
import { LanguageStringControl } from "./LanguageStringControl";
import { AmpelControl } from "./AmpelControl";

/**
 * Controller for binding controls tu model
 */
export class Controller {

    /**
     * Save event
     */
    get onSave() {
        return this._onSave.asEvent();
    }
    private _onSave = new EventDispatcher<Controller, SaveEventArgs>();

    /** collection of all controls */
    private controls: Array<BaseControl> = [];

    /**
     * Constructor
     * @param className Class name for automatic control creation.
     * @param automaticUpdateModel If true: changes are stored in model and save event is fired, if false: use updateModelFromControls() to store new values in model.
     */
    constructor(className: string, public automaticUpdateModel: boolean, element: HTMLElement = null) {
        if (element) {
            this.addControlByElement(element, element.getAttribute("data-field"));
        }

        else if (className && className.length > 0) {
            let elementList = document.getElementsByClassName(className);
            for (let i: number = 0; i < elementList.length; i++) {
                let element = <HTMLElement>elementList[i];
                let control = this.addControlByElement(element, element.getAttribute("data-field"));
            }
        }
    }

    /**
     * Add a HTML control to the list of inline controls.
     * @param elementId The id of the HTML control to add.
     * @param field The name of the model field.
    */
    public addControlById(elementId: string, field: string, validationMessage?: string): BaseControl {
        return this.addControlByElement(document.getElementById(elementId), field, null, validationMessage);
    }

    /**
     * Add a HTML control to the list of inline controls.
     * @param element The HTML control to add.
     * @param field The name of the model field.
     */
    public addControlByElement(element: HTMLElement, field: string, flag: string = null, validationMessage?: string): BaseControl {
        let result: BaseControl = null;
        switch (element.tagName) {
            case "input":
            case "INPUT":
                switch ((element as HTMLInputElement).type) {
                    case "text":
                        result = new TextControl(element as HTMLInputElement, this, field);
                        break;
                    case "date":
                        result = new DateControl(element as HTMLInputElement, this, field);
                        break;
                    case "checkbox":
                        if (flag == "image") {
                            result = new ImageCheckControl(element as HTMLInputElement, this, field);
                        }
                        else {
                            result = new CheckBoxControl(element as HTMLInputElement, this, field);
                        }
                        break;
                    case "number":
                        result = new NumberControl(element as HTMLInputElement, this, field);
                        break;
                }
                break;
            case "textarea":
            case "TEXTAREA":
                result = new MultilineControl(element as HTMLTextAreaElement, this, field);
                break;
            case "select":
            case "SELECT":
                result = new SelectControl(element as HTMLSelectElement, this, field, validationMessage);
                break;
            case "div":
            case "DIV":
                if (element.getAttribute("data-type") == "ampel") {
                    result = new AmpelControl(element, this, field);
                }
                break;
        }
        if (result) {
            this.controls.push(result);
        }
        return result;
    }

    /**
     * Add a radio button group to the list of inline controls.
     * @param elementIds Array of element ids to add.
     */
    public addRadioButtonGroupById(elementIds: Array<string>, field: string, isBoolean: boolean): BaseControl {
        let elements: Array<HTMLInputElement> = [];
        for (let elementId of elementIds) {
            elements.push(<HTMLInputElement>document.getElementById(elementId));
        }
        return this.addRadioButtonGroupByElement(elements, field, isBoolean);
    }

    /**
     * Add a radio button group to the list of inline controls.
     * @param elements Array of input elements to add.
     */
    public addRadioButtonGroupByElement(elements: Array<HTMLInputElement>, field: string, isBoolean: boolean): BaseControl {
        let result = new RadioGroupControl(elements, this, field, isBoolean);
        this.controls.push(result);
        return result;
    }

    public addLanguageStringControl(elementId: string, field: string, isMultiLine: boolean, validationMessage: string = null): BaseControl {
        let result = new LanguageStringControl(elementId, this, field, isMultiLine, validationMessage);
        this.controls.push(result);
        return result;
    }

    /**
     * Set a new model for all controls.
     * @param model THe new model.
     */
    public setModel(model: any, updateModel?: boolean) {
        this.controls.forEach((control: BaseControl, index: number, array: BaseControl[]) => {
            control.setModel(model, updateModel);
        });
    }

    public validate() {
        this.controls.forEach((control: BaseControl, index: number, array: BaseControl[]) => {
            control.validate();
        });
    }

    /**
     * Dispatch a save event.
     * @param args Structure of SaveEventArgs.
     */
    public dispatchOnSave(args: SaveEventArgs) {
        this._onSave.dispatch(this, args);
    }

    /**
     * Store all values from controls in model.
     */
    public updateModelFromControls() {
        this.controls.forEach((control: BaseControl, index: number, array: BaseControl[]) => {
            control.updateModelFromControl();
        });
    }

    /*
     * remove all event listeners.
     */
    public dispose() {
        this.controls.forEach((control: BaseControl, index: number, array: BaseControl[]) => {
            control.dispose();
        });
    }
}
